import { Article } from '../types';

export const calculateElectricityConsumption: Article = {
    id: 'calculate-electricity-consumption',
    title: 'How to Calculate Your Home\'s Electricity Consumption (With Formula)',
    slug: 'how-to-calculate-home-electricity-consumption',
    excerpt: 'Stop guessing your energy needs. Learn the mathematical formula to calculate your total watt-hours and size your solar/inverter system correctly.',
    category: 'Power Generation',
    date: 'April 1, 2026',
    readTime: '2 min read',
    author: 'power.ng Research',
    trending: true,
    keywords: ['calculate electricity bill', 'how to size inverter', 'watt hours formula', 'Nigeria electricity cost', 'appliance wattage'],
    metaDescription: 'Learn exactly how to calculate your daily electricity consumption (kWh) using simple math. Perfect for estimating estimated billing and sizing solar inverters.',
    content: `## Quick Answer: The Consumption Formula
To calculate your daily energy consumption in Kilowatt-hours (kWh), use this simple formula:
**(Appliance Wattage × Hours used per day) ÷ 1000 = Daily kWh consumption.**
Do this for every appliance and add them together to understand exactly how much electricity you consume, allowing you to estimate your DisCo bill or size a solar system.

---

## Why You Need to Know Your Numbers

Whether you are trying to verify if your prepaid meter is running too fast, or telling a solar installer what size of inverter you need, understanding your daily consumption in **Watts (W)** and **Kilowatt-hours (kWh)** is essential.

### Step 1: Find the Wattage of Your Appliances
Look at the label on the back or bottom of your appliances.
* Typical LED Bulb: ` + "`" + `10W` + "`" + `
* Typical Ceiling Fan: ` + "`" + `60W` + "`" + `
* TV (55 inch LED): ` + "`" + `100W` + "`" + `
* Refrigerator: ` + "`" + `250W` + "`" + `
* 1.5HP Air Conditioner: ` + "`" + `1,200W` + "`" + `
* Pumping Machine (1HP): ` + "`" + `750W` + "`" + `

### Step 2: Apply the Formula
Let’s calculate a small living room scenario for a daily cycle:

1. **Four LED Bulbs (10W each) left on for 6 hours:**
   * 40W total × 6 hours = **240 Watt-hours**
2. **One TV (100W) used for 5 hours:**
   * 100W × 5 hours = **500 Watt-hours**
3. **One 1.5HP AC (1200W) used for 4 hours at night:**
   * 1200W × 4 hours = **4,800 Watt-hours**

### Step 3: Convert to Kilowatt-hours (kWh)
Your DisCo bill or your prepaid meter charges you in **kWh**. To find this, divide your total Watt-hours by 1,000.
* Total from our example: 240 + 500 + 4,800 = **5,540 Watt-hours**.
* Calculation: 5,540 ÷ 1000 = **5.54 kWh per day**.

### Step 4: Calculate Your Daily Cost
Once you have your daily kWh, multiply it by your Tariff Band rate.
* If you are on **Band A** paying ₦225/kWh.
* 5.54 kWh × ₦225 = **₦1,246 per day.**

---

## How This Helps Size Your Inverter

If your installer asks, "What load are you backing up?", you now know.
To back up the example above, your peak continuous load (if everything is turned on at the exact same exact second) is **1,340 Watts (1.34kW)**. Therefore, a standard **2.5kVA or 3kVA inverter** will comfortably handle the power spikes, and a **5kWh Lithium battery** would run that setup smoothly throughout the night.`
  };
